import { RemixBrowser, useLocation, useMatches } from '@remix-run/react';
import { captureMessage, init, replayIntegration, browserTracingIntegration, setTag } from '@sentry/remix';
import { startTransition, StrictMode, useEffect } from 'react';
import { hydrateRoot } from 'react-dom/client';

import { SENTRY_DSN } from '~/sentryDsn';
import { installSingleFetchResilience, isSingleFetchUrl, type RecoveryReport } from '~/utils/singleFetchResilience';

// Chrome / Google Translate (and some extensions) rewrite our DOM: they wrap text nodes
// in <font> elements, so a node React still holds a reference to is no longer a child of
// the parent React expects. React's commit-phase removeChild/insertBefore then throw
// NotFoundError and take down the whole page (facebook/react#11538). The DOM is already
// corrupted by the time the error fires, so it can't be caught and recovered inside React
// — instead we make the two DOM methods React relies on degrade gracefully: when the
// target node isn't where React thinks it is, no-op (removeChild) or append (insertBefore)
// instead of throwing. Normal DOM operations are untouched — the guard only changes the
// exact case that used to crash — so React keeps rendering and Google Translate keeps
// working. Community-vetted workaround from a React core member in facebook/react#11538.
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(this: Node, newNode: T, referenceNode: Node | null): T {
    if (referenceNode !== null && referenceNode.parentNode !== this) {
      // Reference node was moved out of this parent by the translator — append instead of
      // throwing, so the new node still lands in the tree.
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

const isProd = import.meta.env.PROD;

// Hydration mismatches in this app are almost always caused by browser
// extensions (Grammarly, password managers, translators, ad-blockers …)
// mutating the DOM before React hydrates — we hydrate the whole `document`, so
// any pre-hydration DOM change trips it. React recovers by client-rendering, so
// these are unactionable user-side noise, NOT bugs in our code.
//
// Strategy: keep them fully visible in dev (so a genuine hydration bug of ours
// surfaces while developing in a clean browser), and sample them down to ~1% in
// prod (so extension noise is negligible but a real site-wide regression still
// produces a signal we can't miss).
const HYDRATION_NOISE = [
  'Hydration failed because the initial UI does not match',
  'There was an error while hydrating',
  'error occurred during hydration',
  'Expected server HTML to contain a matching',
  'Text content does not match server-rendered HTML',
  // React-internal dispatcher cascade after a failed hydration:
  'dispatcher is null', // Firefox phrasing
  "null (reading 'useState')", // Chrome phrasing
  "null (reading 'useMemo')",
  "null (reading 'useEffect')",
  "null (reading 'useRef')",
  "null (reading 'useContext')",
  "null (reading 'useCallback')",
  // Minified React error codes (prod builds): 418/421/423/425 = hydration
  'Minified React error #418',
  'Minified React error #421',
  'Minified React error #423',
  'Minified React error #425',
];

function isHydrationNoise(event: Parameters<NonNullable<Parameters<typeof init>[0]['beforeSend']>>[0]): boolean {
  const haystack = [event.message, ...(event.exception?.values ?? []).map((v) => `${v.type ?? ''}: ${v.value ?? ''}`)]
    .filter(Boolean)
    .join(' ');
  return HYDRATION_NOISE.some((needle) => haystack.includes(needle));
}

init({
  dsn: SENTRY_DSN,
  environment: isProd ? 'production' : 'development',
  enableLogs: true,

  // Full tracing/replay in dev for testing; sampled down for prod traffic.
  tracesSampleRate: isProd ? 0.1 : 1,
  replaysSessionSampleRate: isProd ? 0.1 : 0,
  replaysOnErrorSampleRate: 1,

  integrations: [
    browserTracingIntegration({
      useEffect,
      useLocation,
      useMatches,
    }),
    replayIntegration({
      maskAllText: true,
      blockAllMedia: true,
    }),
  ],

  beforeSend(event) {
    if (isHydrationNoise(event)) {
      // Keep everything in dev; sample ~1% in prod.
      if (!isProd) return event;
      return Math.random() < 0.01 ? event : null;
    }
    return event;
  },
});

type NetworkInformationLike = { effectiveType?: string; downlink?: number; rtt?: number; saveData?: boolean };

function connectionInfo(): NetworkInformationLike | undefined {
  try {
    return (navigator as Navigator & { connection?: NetworkInformationLike }).connection;
  } catch {
    return undefined;
  }
}

function documentProtocol(): string {
  try {
    const [entry] = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    return entry?.nextHopProtocol || 'unknown';
  } catch {
    return 'unknown';
  }
}

type ProtocolSample = { protocol: string; startTime: number };

const dataProtocols = new Map<string, ProtocolSample>();
let lastDataProtocol: ProtocolSample | undefined;

function recordDataProtocol(entry: PerformanceEntry): void {
  try {
    const url = new URL(entry.name);
    if (!isSingleFetchUrl(url)) return;
    const sample = {
      protocol: (entry as PerformanceResourceTiming).nextHopProtocol || 'unknown',
      startTime: entry.startTime,
    };
    const previous = dataProtocols.get(url.pathname);
    if (!previous || sample.startTime >= previous.startTime) dataProtocols.set(url.pathname, sample);
    if (!lastDataProtocol || sample.startTime >= lastDataProtocol.startTime) lastDataProtocol = sample;
  } catch {
    // An entry whose name is not a URL cannot be a single-fetch request.
  }
}

function observeDataProtocols(): void {
  if (typeof PerformanceObserver === 'undefined') return;
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) recordDataProtocol(entry);
    });
    try {
      observer.observe({ type: 'resource', buffered: true });
    } catch {
      observer.observe({ entryTypes: ['resource'] });
    }
  } catch {
    // A missing or throwing observer just leaves the registry empty.
  }
}

/** The protocol of the newest recorded request for `pathname` within the report's time window. */
function resourceProtocol(pathname: string, elapsedMs: number): string {
  const sample = dataProtocols.get(pathname);
  return sample && sample.startTime >= performance.now() - elapsedMs - 1000 ? sample.protocol : 'unknown';
}

// Eager route discovery retries on every DOM mutation, so an unreachable origin would otherwise
// stream events for as long as the tab is open.
const CAPTURE_BUDGET = 10;
let reportsSeen = 0;

function reportRecovery(report: RecoveryReport): void {
  const extra: Record<string, unknown> = { ...report };
  try {
    const connection = connectionInfo();
    const latest = lastDataProtocol;
    extra.dataProtocol = resourceProtocol(report.url, report.elapsedMs);
    extra.lastDataProtocol = latest?.protocol ?? 'unknown';
    extra.lastDataProtocolAgeMs = latest ? Math.round(performance.now() - latest.startTime) : null;
    extra.online = navigator.onLine;
    extra.visibility = document.visibilityState;
    extra.sinceLoadMs = Math.round(performance.now());
    extra.effectiveType = connection?.effectiveType;
    extra.downlink = connection?.downlink;
    extra.rtt = connection?.rtt;
  } catch {
    // Enrichment is best-effort: the report itself still goes out with whatever was collected.
  }
  console.warn('[single-fetch]', extra);

  reportsSeen += 1;
  if (reportsSeen > CAPTURE_BUDGET) return;

  const tags: Record<string, string> = {
    'sf.kind': report.kind,
    'sf.reason': report.reason,
    'net.data_protocol': String(extra.dataProtocol ?? 'unknown'),
  };
  if (report.status !== undefined) tags['sf.status'] = String(report.status);

  captureMessage(`single-fetch ${report.kind}: ${report.reason}`, {
    level: report.kind === 'recovered' ? 'info' : 'warning',
    tags,
    extra,
  });
}

try {
  setTag('net.protocol', documentProtocol());
  const effectiveType = connectionInfo()?.effectiveType;
  if (effectiveType) setTag('net.effective_type', effectiveType);
  observeDataProtocols();
} catch {
  // Telemetry setup must never block hydration.
}

// Installed after Sentry.init so each physical attempt still shows up as a fetch breadcrumb.
installSingleFetchResilience({ report: reportRecovery });

// With v3_singleFetch, RemixBrowser suspends on its first render until the inline
// turbo-stream state is decoded, and nothing above it can be a <Suspense> (we hydrate the
// whole document). A click in that window forces a synchronous hydration: the suspension
// becomes an error, React's retry suspends again with no boundary and it aborts with
// "Root did not complete" (remix-run/remix#10214), leaving server HTML without handlers.
// Calling RemixBrowser once before hydrating starts that decode and throws its promise
// before touching any hook; hydrating after it settles means the first render no longer
// suspends. Relies on @remix-run/react's browser.js internals — re-check on upgrade.
async function decodeSingleFetchState(): Promise<void> {
  if (!window.__remixContext?.future?.v3_singleFetch || !window.__remixContext.stream) return;
  try {
    RemixBrowser({});
  } catch (thrown) {
    if (thrown && typeof (thrown as PromiseLike<unknown>).then === 'function') {
      await thrown;
      return;
    }
  }
  if (!isProd) {
    console.warn('RemixBrowser no longer suspends on state decoding — the pre-hydration decode is a no-op.');
  }
}

function hydrate() {
  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <RemixBrowser />
      </StrictMode>
    );
  });
}

decodeSingleFetchState().then(hydrate, hydrate);
